import React, { useCallback, useEffect, useState, lazy, Suspense } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import { useGlobal } from '../global/GlobalContext';
import { FaTruck } from 'react-icons/fa6';
import { FiTag, FiX, FiTrash2, FiChevronRight, FiShoppingBag, FiCheck } from 'react-icons/fi';
import { BiSolidOffer } from 'react-icons/bi';
import SimpleLayout from '../layout/SimpleLayout';
import Skeleton from 'react-loading-skeleton';
import compressImage from '../utils/compressImage';
import { UserRole } from '../config/userRole';

const Cart = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [openCoupon, setOpenCoupon] = useState(false);
  const [loading, setLoading] = useState(false);
  const { cart, setCart, token, cartLength, cartLoading, wishlistLoading, totalMRP, subtotal, savings } = useGlobal();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);
  const WishlistSlider = lazy(() => import('../components/WishlistSlider'));
  const [couponCode, setCouponCode] = useState("");
  const [status, setStatus] = useState(null);
  const [couponData, setCouponData] = useState([]);
  const [defaultCoupon, setDefaultCoupon] = useState([]);
  const [removingId, setRemovingId] = useState(null);

  const getItemVariation = useCallback((item) => {
    if (item?.product_variation) return item.product_variation;
    if (Array.isArray(item?.product_variations) && item?.prod_variation_id) {
      return item.product_variations.find((v) => v.id === item.prod_variation_id) || null;
    }
    return null;
  }, []);

  const getPackBaseQty = useCallback((item) => {
    const variation = getItemVariation(item);
    const packQty = Number(variation?.pack_qty || item?.pack_qty || 0);
    return packQty > 0 ? packQty : 10;
  }, [getItemVariation]);

  const getItemUnitPrice = useCallback((item) => {
    if (UserRole.userRole === 'distributor') {
      if (item?.price != null) return Number(item.price);
      const variation = getItemVariation(item);
      const packQty = Number(variation?.pack_qty || item?.pack_qty || item?.product?.pack_qty || 0);
      const packPrice = Number(variation?.pack_price || item?.pack_price || item?.product?.pack_price || 0);
      if (packQty > 0 && packPrice > 0) {
        return packPrice / packQty;
      }
    }
    return Number(item.sale_price || 0);
  }, [getItemVariation]);

  const getItemPackInfo = useCallback((item) => {
    const variation = getItemVariation(item);
    const packQty = Number(variation?.pack_qty || item?.pack_qty || item?.product?.pack_qty || 0);
    const packPrice = Number(variation?.pack_price || item?.pack_price || item?.product?.pack_price || 0);
    return { packQty, packPrice };
  }, [getItemVariation]);

  const getFinalAmount = () => {
    let discount = 0;
    if (couponData && subtotal >= couponData.minimum_order_value) {
      discount = couponData.discount_type === "Percentage"
        ? (subtotal * couponData.discount_value) / 100
        : couponData.discount_value;
      discount = Math.min(discount, couponData.maximum_discount || discount); // Avoid NaN
    }
    return {
      finalAmount: subtotal - discount,
      discount
    };
  }

  const { finalAmount, discount } = getFinalAmount();

  const selectQuantity = async (itemId, variationId, quantity, price) => {
    setLoading(true);
    try {
      // Send the selected quantity to the backend to update the cart
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/update_to_cart`,
        {
          product_id: itemId,
          prod_variation_id: variationId,
          quantity,
          price,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (localStorage.getItem('localCart')) {
        let localCart = JSON.parse(localStorage.getItem('localCart') || '[]');
        // Update the local cart with the new quantity
        localCart = localCart.map((item) =>
          item.id === itemId && item.prod_variation_id === variationId
            ? { ...item, quantity, price }
            : item
        );
        // Save the updated cart back to localStorage
        localStorage.setItem('localCart', JSON.stringify(localCart));
      }
      setSelectedQuantity(null);
    } catch (error) {
      console.error('Error updating quantity:', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Remove Item from Cart
  const removeFromCart = async (cartId) => {
    const result = await Swal.fire({
      title: 'Remove item?',
      text: 'This item will be removed from your bag.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'rgb(var(--brand-600))',
    });
    if (!result.isConfirmed) return;

    const userToken = localStorage.getItem('userToken');
    setRemovingId(cartId);
    try {
      // If user is logged in, make API call to remove from backend cart
      if (userToken) {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/delete_cart_item/${cartId}`, {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        if (res.data.status) {
          setCart(prevCart => prevCart.filter(item => item.id !== cartId));
        }
      } else {
        let localCart = JSON.parse(localStorage.getItem('localCart') || '[]');
        localCart = localCart.filter(item => item.id !== cartId);
        localStorage.setItem('localCart', JSON.stringify(localCart));
        setCart(localCart);
      }
    } catch (error) {
      console.error('Error removing item from cart:', error.response?.data?.message || error.message);
    } finally {
      setRemovingId(null);
    }
  };

  useEffect(() => {
    const fetchCoupon = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_suggestion_coupons`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        })

        if (response.data.status) {
          setDefaultCoupon(response.data.coupons);
        }
      } catch (error45) {
        console.error(error45);
      }
    }

    fetchCoupon();
  }, [])


  const handleApplyCoupon = async (e) => {
    if (!couponCode) return;
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/validate_coupon`, {
        'coupon_code': couponCode,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      }
      );
      const data = response.data;
      if (data.status) {
        if (subtotal >= response.data.data?.minimum_order_value) {
          setCouponData(data.data);
          setOpenCoupon(false);
        } else {
          setStatus({ type: "error", message: `Your cart must be required ₹${response.data.data?.minimum_order_value} amount to apply this coupon` });
        }
      } else {
        setStatus({ type: "error", message: `${response.data.message}` });
      }
    } catch (error) {
      setStatus({ type: "error", message: "This is not a valid Coupon code!" });
    } finally {
      setLoading(false);
    }
  };

  const proceedHandler = () => {
    if (localStorage.getItem('userToken') && finalAmount > 0) {
      navigate('/checkout', { state: { finalAmount, discount, couponCode } });
    } else {
      navigate('/login', { state: { slug: '/cart' } });
    }
  }

  useEffect(() => {
    if (isPopupOpen || openCoupon) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isPopupOpen, openCoupon]);

  const popupQuantities = UserRole.userRole === 'distributor'
    ? Array.from({ length: 10 }, (_, i) => (i + 1) * getPackBaseQty(currentItem))
    : Array.from({ length: 10 }, (_, i) => i + 1);


  return (
    <SimpleLayout title={`My Bag (${cartLength} ${cartLength > 1 ? 'Items' : 'Item'})`} link='javascript:;'>
      <div className="cart-page bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {cartLoading ? (
            <div className="animate-pulse">
              <div className="h-7 w-48 bg-gray-200 rounded-lg mb-6" />
              <div className="flex lg:flex-row flex-col gap-6">
                <div className="lg:w-2/3 w-full space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm flex gap-4">
                      <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-gray-200 flex-shrink-0" />
                      <div className="flex-1 space-y-2.5 py-1">
                        <div className="h-3 w-20 bg-gray-200 rounded" />
                        <div className="h-4 w-3/4 bg-gray-200 rounded" />
                        <div className="h-3 w-28 bg-gray-200 rounded" />
                        <div className="h-6 w-24 bg-gray-200 rounded mt-4" />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="lg:w-1/3 w-full space-y-4">
                  <div className="h-20 bg-gray-200 rounded-2xl" />
                  <div className="h-64 bg-gray-200 rounded-2xl" />
                </div>
              </div>
            </div>
          ) : cartLength > 0 ? (
            <>
              <div className="flex items-center gap-2.5 mb-6">
                <FiShoppingBag className="text-brand-600" size={22} />
                <h2 className="text-xl font-bold text-gray-900">
                  My Bag <span className="text-gray-400 font-medium">({cartLength} {cartLength > 1 ? 'Items' : 'Item'})</span>
                </h2>
              </div>

              <div className="flex lg:flex-row flex-col gap-6">
                {/* ── Items column ────────────────────────────────────────── */}
                <div className="cart-items lg:w-2/3 w-full">
                  {loading ? (
                    <Skeleton height={44} className="rounded-xl mb-4" />
                  ) : savings > 0 ? (
                    <div className="mb-4 flex items-center gap-3 rounded-2xl border border-green-100 bg-green-50 px-4 py-3 text-sm font-medium text-green-800">
                      <img className="w-5 h-5" loading="lazy" src="images/percentage.gif" alt="" />
                      You are saving ₹{savings.toFixed ? savings.toFixed(2) : savings} on this order
                    </div>
                  ) : null}

                  <div className="space-y-4">
                    {cart.map((item) => (
                      item ? (
                        <div
                          key={item.id}
                          className="relative rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                        >
                          <button
                            onClick={() => removeFromCart(item.id)}
                            disabled={removingId === item.id}
                            aria-label="Remove item"
                            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                          >
                            {removingId === item.id ? (
                              <span className="w-3.5 h-3.5 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
                            ) : (
                              <FiTrash2 size={15} />
                            )}
                          </button>

                          {loading ? (
                            <Skeleton height={96} width={96} className="rounded-xl" />
                          ) : (
                            <div className="flex gap-4">
                              <Link
                                to={`/p/${item.slug ? item.slug : item.product.slug}`}
                                className="flex-shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-gray-50"
                              >
                                <img
                                  src={compressImage(item.primary_img, 400, 70, 'webp')}
                                  alt={item.prod_name}
                                  className="w-full h-full object-cover object-top"
                                />
                              </Link>

                              <div className="flex-1 min-w-0 pr-8">
                                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
                                  {localStorage.getItem('userToken')
                                    ? item.product.brand_details.brand_name
                                    : item.brand_details.brand_name}
                                </h2>
                                <h3 className="text-sm font-semibold text-gray-900 mt-0.5 line-clamp-2">{item.prod_name}</h3>
                                <p className="text-gray-500 text-xs flex items-center gap-1.5 mt-1.5">
                                  <FaTruck className="text-green-600" size={12} /> Ships in 2-3 days
                                </p>

                                <div className="flex flex-wrap items-center gap-2 mt-3">
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-semibold">
                                    Size: {item.product_variation?.size
                                      ?? item.product_variations?.find((size) => size.id === item.prod_variation_id)?.size
                                      ?? '—'}
                                  </span>
                                  <button
                                    onClick={() => {
                                      setCurrentItem(item);
                                      setIsPopupOpen(true);
                                    }}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-brand-600/10 text-brand-600 text-[11px] font-semibold hover:bg-brand-600/20 transition-colors"
                                  >
                                    Qty: {item.quantity} <FiChevronRight size={11} />
                                  </button>
                                </div>

                                <div className="mt-3 flex items-end justify-between">
                                  {UserRole.userRole !== 'distributor' ? (
                                    <div>
                                      <div className="flex items-baseline gap-2">
                                        <p className="text-gray-900 font-bold text-base">₹{(item.sale_price * item.quantity).toLocaleString('en-IN')}</p>
                                        {item.regular_price && (
                                          <p className="text-gray-400 line-through text-xs">
                                            ₹{(item.regular_price * item.quantity).toLocaleString('en-IN')}
                                          </p>
                                        )}
                                      </div>
                                      {item.regular_price > item.sale_price && (
                                        <p className="text-green-700 text-xs font-medium mt-0.5">
                                          You saved ₹{((item.regular_price - item.sale_price) * item.quantity).toLocaleString('en-IN')}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    (() => {
                                      const { packQty, packPrice } = getItemPackInfo(item);
                                      const lineTotal = packQty > 0 && packPrice > 0
                                        ? (Number(item.quantity) / packQty) * packPrice
                                        : getItemUnitPrice(item) * Number(item.quantity);
                                      return (
                                        <div>
                                          <p className="text-gray-900 font-bold text-base">₹{lineTotal.toFixed(2)}</p>
                                          {packQty > 0 && packPrice > 0 && (
                                            <p className="text-gray-400 text-[11px] mt-0.5">Pack: ₹{packPrice} / {packQty} pcs</p>
                                          )}
                                        </div>
                                      );
                                    })()
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div key={item.id} className="cart-item rounded-2xl border border-gray-100 bg-white p-4 text-sm text-gray-400">
                          Product details not available
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* ── Quantity popup ──────────────────────────────────────── */}
                {isPopupOpen && (
                  <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 px-4">
                    <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-sm">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-base font-bold text-gray-900">Select Quantity</h2>
                        <button
                          onClick={() => { setIsPopupOpen(false); setSelectedQuantity(null); }}
                          className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
                        >
                          <FiX size={16} />
                        </button>
                      </div>

                      <div className="grid grid-cols-5 gap-2 mb-5">
                        {popupQuantities.map((qty) => (
                          <button
                            type="button"
                            key={qty}
                            onClick={() => setSelectedQuantity(qty)}
                            className={`h-10 flex items-center justify-center rounded-xl text-sm font-semibold border transition-colors
                                          ${selectedQuantity === qty
                                ? 'bg-brand-600 text-white border-brand-600'
                                : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-300'}`}
                          >
    {qty}
                          </button>
                        ))}
                      </div>

                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => { setIsPopupOpen(false); setSelectedQuantity(null); }}
                          className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            if (currentItem) {
                              setIsLoading(true);
                              try {
                                await selectQuantity(
                                  currentItem.product_id,
                                  currentItem.prod_variation_id,
                                  selectedQuantity,
                                  getItemUnitPrice(currentItem)
                                );

                                setCart((prevCart) =>
                                  prevCart.map((item) =>
                                    item.id === currentItem.id &&
                                      item.prod_variation_id === currentItem.prod_variation_id
                                      ? { ...item, quantity: selectedQuantity, price: getItemUnitPrice(currentItem) }
                                      : item
                                  )
                                );

                                setIsPopupOpen(false);
                                setSelectedQuantity(null);
                              } catch (error) {
                                console.error("Error while selecting quantity:", error);
                              } finally {
                                setIsLoading(false);
                              }
                            }
                          }}
                          disabled={isLoading || !selectedQuantity}
                          className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isLoading ? "Updating..." : "Update"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Summary column ──────────────────────────────────────── */}
                {loading ? (
                  <div className="price-summary lg:w-1/3 w-full">
                    <Skeleton height={220} className="rounded-2xl" />
                  </div>
                ) : (
                  <div className="price-summary lg:w-1/3 w-full lg:sticky lg:top-4 self-start space-y-4">
                    <button
                      onClick={() => setOpenCoupon(true)}
                      className="w-full flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-4 shadow-sm hover:border-gray-200 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                          <FiTag className="text-orange-500" size={16} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Apply Coupon / Gift Card</p>
                          <p className="text-gray-400 text-xs mt-0.5">Crazy deals and other amazing offers</p>
                        </div>
                      </div>
                      <span className="text-brand-600 text-sm font-semibold flex-shrink-0">View</span>
                    </button>

                    {discount > 0 && (
                      <div className="relative rounded-2xl border border-green-100 bg-gradient-to-b from-green-50 to-white p-4 flex items-center justify-between">
                        <span className="absolute -top-2.5 left-6 rounded-full px-2.5 py-0.5 text-[10px] font-bold text-white bg-badge-orange">Best offer applied</span>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                            <BiSolidOffer className="text-green-600" size={17} />
                          </div>
                          <p className="text-sm">
                            <span className="font-semibold text-gray-900">{couponData.coupon_code}</span>
                            <span className="block text-xs text-gray-500 mt-0.5">-₹{discount.toFixed(2)} discount applied</span>
                          </p>
                        </div>
                        <button onClick={() => setCouponData([])} className="bg-green-600 rounded-full text-white text-xs font-semibold py-1.5 px-3 hover:bg-green-700 transition-colors">
                          Remove
                        </button>
                      </div>
                    )}

                    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                      <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-4">Price Summary</h3>
                      <div className="space-y-2.5 text-sm">
                        <div className="flex justify-between text-gray-600">
                          <span>Total MRP (Incl. of taxes)</span>
                          <span className="font-medium text-gray-800">₹{totalMRP.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Bag Discount</span>
                          <span className="font-medium text-green-600">-₹{savings.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Delivery Fee</span>
                          <span className="font-medium text-green-600">Free</span>
                        </div>
                      </div>

                      <div className="border-t border-dashed border-gray-200 mt-4 pt-4 flex justify-between items-center">
                        <span className="font-bold text-gray-900">Subtotal</span>
                        {discount > 0 ? (
                          <div className="text-right">
                            <span className="block text-gray-400 text-xs line-through">₹{subtotal.toFixed(2)}</span>
                            <span className="text-lg font-bold text-green-700">₹{finalAmount.toFixed(2)}</span>
                          </div>
                        ) : (
                          <span className="text-lg font-bold text-gray-900">₹{subtotal.toFixed(2)}</span>
                        )}
                      </div>

                      <p className="mt-3 text-xs font-medium text-center text-gray-500">
                        Yayy! You get <span className="text-green-600 font-semibold">FREE</span> delivery on this order
                      </p>

                      <button
                        onClick={() => proceedHandler()}
                        className="mt-4 w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                      >
                        Proceed to Checkout <FiChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Coupon modal ────────────────────────────────────────── */}
              {openCoupon && (
                <div className="bg-black/50 fixed inset-0 flex justify-center items-end sm:items-center z-50 px-0 sm:px-4">
                  <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto">
                    <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
                      <h2 className="text-base font-bold text-gray-900">Coupons & Offers</h2>
                      <button
                        onClick={() => setOpenCoupon(false)}
                        className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
                      >
                        <FiX size={18} />
                      </button>
                    </div>

                    <div className="p-5">
                      <form onSubmit={handleApplyCoupon} className="flex gap-2">
                        <input
                          name="coupon-code"
                          type="text"
                          placeholder="Enter coupon code"
                          className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-600 transition-colors"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                        />
                        <button
                          type="submit"
                          className="bg-brand-600 hover:bg-brand-700 text-sm text-white font-semibold px-5 py-2.5 rounded-xl transition-colors disabled:opacity-50"
                          disabled={loading}
                        >
                          {loading ? "Applying..." : "Apply"}
                        </button>
                      </form>

                      {status && (
                        <p className={`mt-3 text-sm ${status.type === "success" ? "text-green-600" : "text-red-600"}`}>
                          {status.message}
                        </p>
                      )}

                      <div className="mt-6">
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Available Coupons</h4>
                        <div className="space-y-2.5">
                          {defaultCoupon.map((item) => (
                            <div
                              key={item.id}
                              onClick={() => {
                                setCouponCode(item.coupon_code);
                                handleApplyCoupon({ preventDefault: () => { } });
                              }}
                              className="cursor-pointer border border-gray-200 hover:border-brand-600 transition-colors rounded-xl p-3.5 flex justify-between items-center group"
                            >
                              <div>
                                <div className="text-sm font-bold text-gray-800">
                                  {item.coupon_code}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {item.description || "Tap to apply this coupon"}
                                </div>
                              </div>
                              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 group-hover:bg-brand-600 group-hover:text-white transition-colors flex-shrink-0">
                                <FiCheck size={14} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <img alt='cart-png' loading="lazy" className='w-64 sm:w-80 m-auto' src='images/bag.png' />
              <p className="text-xl font-bold text-gray-900 mt-4">Hey, your bag feels a bit empty!</p>
              <p className="text-gray-500 mt-2">
                Let's add some awesome items to it!
              </p>

              <button
                onClick={() => navigate('/products')}
                className="mt-6 bg-brand-600 hover:bg-brand-700 text-white font-semibold px-8 py-3 rounded-xl transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          )}
        </div>

        {localStorage.getItem('userToken') && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
            {wishlistLoading ? (
              <div className="space-y-4">
                <div className="h-24 bg-gray-200 animate-pulse rounded-2xl"></div>
                <div className="h-24 bg-gray-200 animate-pulse rounded-2xl"></div>
                <div className="h-24 bg-gray-200 animate-pulse rounded-2xl"></div>
              </div>
            ) : (
              <Suspense fallback={<div>Loading...</div>}>
                <WishlistSlider />
              </Suspense>
            )}
          </div>
        )}
      </div>
    </SimpleLayout>
  );
};

export default Cart;
